"use client";

import Link from "next/link";
import {
  BookOpen,
  Paintbrush2,
  Bolt,
  Package,
  ScanSearch,
  Shirt,
  Swords,
  FileText,
  ChevronRight,
  ExternalLink,
  Wrench,
  Car,
  PlayCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { LegalFooter } from "@/components/LegalFooter";
import { Callout, Tag } from "@/components/GuidesShared";
import { AppHeader } from "@/components/layout/AppHeader";
import { useAuth } from "@/components/auth/AuthProvider";
import { TUTORIAL_VIDEOS_ADMIN_ONLY, TUTORIAL_VIDEOS_HIDDEN } from "@/lib/guides/tutorialVideos";
import { usePreviewAwareAdmin } from "@/lib/home/planPreview";

// ─── Hub cards ────────────────────────────────────────────────────────────────

function HubCard({
  href,
  icon,
  label,
  description,
  items,
  badge,
  cta = "Read guide",
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  items: string[];
  badge?: string;
  cta?: string;
}) {
  return (
    <Link
      href={href}
      className="group flex flex-col gap-5 rounded-[2rem] border border-border/60 bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_24px_60px_hsl(var(--foreground)/0.08)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="rounded-xl border border-primary/15 bg-primary/10 p-3 text-primary">
          {icon}
        </div>
        {badge && (
          <Badge variant="outline" className="rounded-full text-xs">
            {badge}
          </Badge>
        )}
      </div>
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-foreground">{label}</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-1 w-1 shrink-0 rounded-full bg-primary/50" />
            {item}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-1.5 text-sm font-medium text-primary mt-auto pt-2 border-t border-border/40">
        {cta}
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function ToolChip({
  icon,
  name,
  href,
}: {
  icon: React.ReactNode;
  name: string;
  href: string;
}) {
  return (
    <Link
      href={href}
      className="flex items-center gap-2.5 rounded-xl border border-border/60 bg-background px-3.5 py-2.5 text-sm transition-colors hover:border-primary/30 hover:bg-primary/5 group"
    >
      <span className="text-primary/80">{icon}</span>
      <span className="font-medium text-foreground">{name}</span>
      <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
    </Link>
  );
}

// ─── Getting Started section (stays on hub) ───────────────────────────────────

function GettingStarted() {
  return (
    <section className="space-y-6">
      <div className="flex items-center gap-3 border-b border-border/50 pb-4">
        <div className="rounded-xl border border-primary/15 bg-primary/10 p-2.5 text-primary">
          <BookOpen className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-primary/80">Overview</p>
          <h2 className="text-2xl font-semibold tracking-tight">Getting Started</h2>
        </div>
      </div>

      <p className="text-sm leading-7 text-muted-foreground">
        The Workshop is a browser-based suite of tools built for FiveM server developers, vehicle
        creators, and community builders. It replaces a fragmented collection of desktop utilities
        with a single, always-updated platform you can access from any device.
      </p>
      <p className="text-sm leading-7 text-muted-foreground">
        All tools run entirely in the browser — no downloads, no installs. Files you process
        through a tool stay on your machine; uploads are used only for processing and aren't
        retained. The exception is content you choose to share — saved projects, assets, and
        images you attach to guide discussions, which are stored so you can come back to them.
      </p>

      <Callout type="tip">
        Discord sign-in is the fastest path to get started. Your username and avatar carry over
        automatically and no email verification is needed.
      </Callout>

      <div>
        <p className="text-sm font-medium text-foreground mb-3">Subscription plans</p>
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-foreground">Plan</th>
                <th className="px-4 py-3 text-left font-medium text-foreground">Included tools</th>
                <th className="px-4 py-3 text-left font-medium text-foreground">Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40 text-muted-foreground">
              <tr>
                <td className="px-4 py-3">Vehicle Developer</td>
                <td className="px-4 py-3">Paint Booth, UV Studio, Blinklab, Easy Meta</td>
                <td className="px-4 py-3">$14.99/mo</td>
              </tr>
              <tr>
                <td className="px-4 py-3">Server Developer</td>
                <td className="px-4 py-3">PackBench, Code Smith, EUP Viewer, Weapon Designer</td>
                <td className="px-4 py-3">$19.99/mo</td>
              </tr>
              <tr>
                <td className="px-4 py-3">Pro</td>
                <td className="px-4 py-3">All tools</td>
                <td className="px-4 py-3">$29.99/mo</td>
              </tr>
              <tr>
                <td className="px-4 py-3">Studio</td>
                <td className="px-4 py-3">All tools + higher limits + early access</td>
                <td className="px-4 py-3">$49.99/mo</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Subscribe at{" "}
          <a
            href="https://space101.store/pages/workshop-v2"
            target="_blank"
            rel="noreferrer"
            className="text-primary underline underline-offset-4 inline-flex items-center gap-0.5"
          >
            space101.store <ExternalLink className="h-3 w-3" />
          </a>
          . After subscribing, sign out and back in to refresh your access.
        </p>
      </div>

      <div>
        <p className="text-sm font-medium text-foreground mb-3">File upload tips</p>
        <ul className="space-y-2 text-sm text-muted-foreground">
          {[
            <>Use the correct file type for each tool — wrong formats will show an error.</>,
            <>Large <Tag>.ytd</Tag> files (&gt;100 MB) may take a moment to decode in the browser.</>,
            <><Tag>.obj</Tag> / <Tag>.glb</Tag> / <Tag>.yft</Tag> models must include normals for correct lighting in Paint Booth.</>,
            <>ZIP files for PackBench and Code Smith must not be password-protected.</>,
          ].map((tip, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Guides() {
  const { displayIsAdmin: isAdmin } = usePreviewAwareAdmin();
  // Hidden: card removed for everyone. Admin-only: admins only. Public: everyone.
  const showVideosCard = TUTORIAL_VIDEOS_HIDDEN
    ? false
    : TUTORIAL_VIDEOS_ADMIN_ONLY
      ? isAdmin
      : true;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader back={{ href: "/", label: "Back" }} />

      <div className="mx-auto max-w-5xl px-4 pt-12 pb-20 sm:px-6 lg:px-8">
        {/* Page heading */}
        <div className="mb-14">
          <p className="text-xs uppercase tracking-[0.28em] text-primary/80 mb-3">Documentation</p>
          <h1 className="text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
            Workshop Guides
          </h1>
          <p className="mt-4 text-base leading-7 text-muted-foreground max-w-2xl">
            Reference documentation for The Workshop tools and the GTA5 file formats and workflows
            they're built around.
          </p>
        </div>

        {/* Hub cards */}
        <div className="grid gap-5 sm:grid-cols-2 mb-16">
          <HubCard
            href="/guides/tools"
            icon={<Wrench className="h-5 w-5" />}
            label="Tool Guides"
            /* "all 18 Workshop tools" read as a completeness claim and was one:
               there are 18 written guides against 22 creator routes and seven
               free tools, so a reader arriving for the CFG Builder was being
               told the index covers it. "18 of" says what is actually here. */
            description="Step-by-step walkthroughs for 18 of the Workshop tools — from Paint Booth to Code Smith."
            badge="18 tools"
            items={[
              "Paint Booth — 3D livery & texture preview",
              "Blinklab — lightbar pattern editor",
              "Easy Meta — vehicle meta generator",
              "PackBench — FiveM resource builder",
              "Code Smith — resource scanner",
              "EUP Viewer, UV Studio, Weapon Designer",
            ]}
          />
          <HubCard
            href="/guides/gta5"
            icon={<Car className="h-5 w-5" />}
            label="GTA Knowledge"
            description="File formats, vehicle meta breakdowns, FiveM resource structure, EUP slots, the 32 Siren Script, and modkit ID fixes."
            badge="8 topics"
            items={[
              "GTA5 file formats (YFT, YDR, YDD, YTD…)",
              "vehicles.meta, handling.meta, carcols.meta",
              "FiveM fxmanifest.lua structure",
              "EUP component slot IDs",
              "Livery & DDS texture workflow",
              "32 Siren Script — extending siren limits",
              "Fixing modkit ID conflicts",
              "Carcols & carvariations XML",
            ]}
          />
          {showVideosCard && (
            <HubCard
              href="/guides/videos"
              icon={<PlayCircle className="h-5 w-5" />}
              label="Tutorial Videos"
              description={
                TUTORIAL_VIDEOS_ADMIN_ONLY
                  ? "Video walkthroughs for The Workshop tools and workflows. Visible to admins only while we build out the library."
                  : "Video walkthroughs for The Workshop tools and workflows — from importing liveries to packaging a finished resource."
              }
              badge={TUTORIAL_VIDEOS_ADMIN_ONLY ? "Admin only" : undefined}
              cta="Watch videos"
              items={[
                "Embedded video walkthroughs",
                "Step-by-step tool tutorials",
                "New videos added regularly",
              ]}
            />
          )}
        </div>

        {/* Quick tool links */}
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">
            Quick access — open a tool
          </p>
          <div className="grid gap-2.5 grid-cols-2 sm:grid-cols-4">
            <ToolChip icon={<Paintbrush2 className="h-4 w-4" />} name="Paint Booth" href="/paintbooth" />
            <ToolChip icon={<Shirt className="h-4 w-4" />} name="EUP Viewer" href="/eup-viewer" />
            <ToolChip icon={<Bolt className="h-4 w-4" />} name="Blinklab" href="/blinklab" />
            <ToolChip icon={<Paintbrush2 className="h-4 w-4" />} name="UV Studio" href="/uv-studio" />
            <ToolChip icon={<Swords className="h-4 w-4" />} name="Weapon Designer" href="/weapon-viewer" />
            <ToolChip icon={<FileText className="h-4 w-4" />} name="Easy Meta" href="/easy-meta" />
            <ToolChip icon={<Package className="h-4 w-4" />} name="PackBench" href="/pack-bench" />
            <ToolChip icon={<ScanSearch className="h-4 w-4" />} name="Code Smith" href="/code-smith" />
          </div>
        </div>

        {/* Getting Started */}
        <GettingStarted />
      </div>

      <LegalFooter />
    </div>
  );
}
